import { getAuth, getIdToken, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import type * as React from 'react';

import { Logger, Trace } from '@gitbook/util-debug';
import { createCacheSingleton } from '@gitbook/util-suspense-cache';

const logger = Logger('auth');

const initializeAuthCache = createCacheSingleton<Record<string, never>, Record<string, never>>(
    () => {
        const trace = new Trace('auth.initialize');
        return trace.wrap(async () => {
            const auth = getAuth();
            const searchParams = new URLSearchParams(window.location.search);
            const customToken = searchParams.get('auth_token') || undefined;

            if (customToken) {
                // If a token was passed in the url, we strip it.
                window.history.replaceState({}, '', window.location.pathname);

                try {
                    logger.info('authenticating with custom token before subscribing to auth');
                    await signInWithCustomToken(auth, customToken);
                    if (auth.currentUser) {
                        await getIdToken(auth.currentUser, true);
                    }
                } catch (error) {
                    logger.critical('Error during auth with custom token', error);
                }
            }

            await new Promise<void>((resolve, reject) => {
                const unsubscribe = onAuthStateChanged(
                    auth,
                    () => {
                        unsubscribe();
                        resolve();
                    },
                    reject
                );
            });

            return {};
        });
    }
);

/**
 * Initialize the firebase authentication.
 * This function is called at the very beginning of bootstrapping the application.
 * `FirebaseAuthProvider` is then used to wait for it to be ready.
 */
export async function initializeAuth(): Promise<void> {
    await initializeAuthCache.readAsync({});
}

/**
 * Component to suspend rendering while auth is being initialized.
 */
export function FirebaseAuthProvider(props: { children: React.ReactNode }): React.ReactElement {
    const { children } = props;
    initializeAuthCache.read({});

    return <>{children}</>;
}
